export interface HelpAnchorInfo {
  /** The help-path attribute value - the targeting key a tour step uses. */
  path: string;
  /** The help-id attribute, when present. */
  id: string | null;
  /** A short human-readable label (aria-label, title, or visible text). */
  label: string;
  /** The element's lowercased tag name. */
  tag: string;
}

export const HELP_ANCHOR_LABEL_MAX = 80;

/**
 * Enumerates every `help-path`-tagged element in `root` and returns a compact
 * inventory of the page's walkthrough anchors. This is the grounding handed to a
 * Ryn when it generates a tour at runtime: the model can only target paths that
 * actually exist on the page, so generated steps never point at a ghost element.
 *
 * Labels are derived from aria-label, then title, then visible text, with
 * whitespace collapsed and length capped so the inventory stays prompt-friendly.
 */
export function collectHelpAnchors(root: ParentNode = document): HelpAnchorInfo[] {
  const elements = Array.from(root.querySelectorAll<HTMLElement>('[help-path]'));

  return elements.map((el) => {
    const rawLabel =
      el.getAttribute('aria-label') ??
      el.getAttribute('title') ??
      el.textContent ??
      '';
    const label = rawLabel
      .replace(/\s+/g, ' ')
      .trim()
      .slice(0, HELP_ANCHOR_LABEL_MAX);

    return {
      path: el.getAttribute('help-path') ?? '',
      id: el.getAttribute('help-id'),
      label,
      tag: el.tagName.toLowerCase(),
    };
  });
}
